import Vue from 'utils/vueUtils';
import { debounce } from 'lodash';
import RecurringBroadcasts from '../collections/RecurringBroadcasts';
import RecurringBroadcast from '../models/RecurringBroadcast';
import Broadcasts from '../collections/Broadcasts';
import Broadcast from '../models/Broadcast';
import { baseApi } from 'core/api';

const state = () => ({
    _item: null,
    _items: [],
    _pagination: null,
    _parameters: {
        page: 1,
        sortBy: ['disabled', 'created_at'],
        sortOrder: 'desc',
        search: null,
        length: 10,
        filter: null,
        filtersList: [],
    },
    _parametersGlobalItems: {
        statuses: [],
    },
    _loading: false,
    _loadingError: false,
});

const getters = {
    recurringBroadcast: (state) => state._item,
    recurringBroadcasts: (state) => state._items,
    recurringBroadcastsPagination: (state) => state._pagination,
    recurringBroadcastsParameters: (state) => state._parameters,
    recurringBroadcastsLoading: (state) => state._loading,
    recurringBroadcastsLoadingError: (state) => state._loadingError,
};

const utils = {
    recurringBroadcastsStateDeleteItem: (storeInstance, { id }) => {
        const index = storeInstance._items.findIndex((item) => item.id === id);
        const item = storeInstance._items[index];

        if (!item) return;

        storeInstance._items.splice(index, 1);
    },

    recurringBroadcastsStateUpdateItem: (storeInstance, { id, props }) => {
        const index = storeInstance._items.findIndex((item) => item.id === id);
        const item = storeInstance._items[index];

        if (!item) return;

        const newItem = new RecurringBroadcast({
            ...item.data,
            ...props,
        });

        Vue.set(storeInstance._items, index, newItem);
    },

    recurringBroadcastsStateUpdateDeleteItemForItems: (
        storeInstance,
        { idChild, idParent, props, type = 'update' }
    ) => {
        const indexParent = storeInstance._items.findIndex((item) => item.id === idParent);
        const itemParent = storeInstance._items[indexParent];

        if (!itemParent) return;

        const indexChild = itemParent.items.findIndex((item) => item.id === idChild);
        const itemChild = itemParent.items[indexChild];

        if (!itemChild) return;

        if (type === 'update') {
            const newItem = new Broadcast({
                ...itemChild.data,
                ...props,
            });

            Vue.set(itemParent.items, indexChild, newItem);
        }

        if (type === 'delete') {
            itemParent.items.splice(indexChild, 1);
        }
    },

    recurringBroadcastsLoad: (storeInstance, retry) => {
        if (retry === true) {
            storeInstance._setRecurringBroadcastsRetryLoading();
        }

        storeInstance._setRecurringBroadcastsLoading(true);

        return baseApi
            .get('core/broadcasts/recurring', {
                params: storeInstance._parameters,
            })
            .then((response) => {
                storeInstance._setRecurringBroadcastsLoaded({
                    data: response.data,
                });
                return response;
            })
            .catch((err) => {
                storeInstance._setRecurringBroadcastsErrorLoading();
            })
            .finally(() => {
                storeInstance._setRecurringBroadcastsLoading(false);
            });
    },
};

const debounceRecurringBroadcastsLoad = debounce(utils.recurringBroadcastsLoad, 500);

const actions = {
    recurringBroadcastsLoad(retry) {
        this._setRecurringBroadcastsLoading(true);
        return debounceRecurringBroadcastsLoad(this, retry);
    },

    recurringBroadcastsItemsLoad({ item }) {
        this.recurringBroadcastsStateUpdateItem({
            id: item.id,
            props: {
                loading: true,
            },
        });

        const params = {
            ...item.params,
            ...this._parametersGlobalItems,
        };

        return baseApi
            .get(`core/broadcasts/recurring/${item.id}/details`, {
                params: params,
            })
            .then((response) => {
                this._setRecurringBroadcastsItemsLoaded({
                    item: item,
                    data: response.data,
                });
                return response;
            })
            .finally(() => {
                this.recurringBroadcastsStateUpdateItem({
                    id: item.id,
                    props: {
                        loading: false,
                    },
                });
            });
    },

    recurringBroadcastsPageFilters() {
        const params = {
            withCustomFields: true,
        };

        return baseApi.get('core/broadcasts/recurring/filters', { params }).then((response) => {
            return response.data;
        });
    },

    handleRecurringBroadcastAction({ itemChild, itemParent, status }) {
        return baseApi.put(`core/broadcasts/${itemChild.id}/${status}`).then((response) => {
            this._setRecurringBroadcastsStateUpdateDeleteItemForParent({
                idChild: itemChild.id,
                idParent: itemParent.id,
                props: response.data,
            });

            return new Broadcast(response.data);
        });
    },

    recurringBroadcastChildDelete({ form, itemChild, itemParent }) {
        return form.delete(`core/broadcasts/${itemChild.id}`).then(() => {
            this._setRecurringBroadcastsStateUpdateDeleteItemForParent({
                idChild: itemChild.id,
                idParent: itemParent.id,
                props: {},
                type: 'delete',
            });
        });
    },

    recurringBroadcastDelete({ form, item }) {
        return form.delete(`core/broadcasts/recurring/${item.id}/delete`).then(() => {
            this._setRecurringBroadcastsStateDeleteItem({
                id: item.id,
            });
        });
    },

    recurringBroadcastTest({ form }) {
        return form.post('core/broadcasts/test').then((response) => {
            return new Broadcast(response.data);
        });
    },

    recurringBroadcastsUpdateSearch(search) {
        this._setRecurringBroadcastsUpdateSearch(search);
        this.recurringBroadcastsLoad();
    },

    recurringBroadcastsUpdateFilter(filter) {
        this._setRecurringBroadcastsUpdateFilter(filter);
        this.recurringBroadcastsLoad();
    },

    recurringBroadcastsUpdatePage(page) {
        this._setRecurringBroadcastsUpdatePage(page);
        this.recurringBroadcastsLoad();
    },

    recurringBroadcastsUpdateLength(length) {
        this._setRecurringBroadcastsUpdateLength(length);
        this.recurringBroadcastsLoad();
    },

    recurringBroadcastsClearSearch() {
        this._setRecurringBroadcastsSearchCleared();
        this.recurringBroadcastsLoad();
    },

    async recurringBroadcastsPageFiltersApply(filters) {
        this._setRecurringBroadcastsFiltersApply(filters);
        return this.recurringBroadcastsLoad();
    },

    recurringBroadcastsResetParameters() {
        this._setRecurringBroadcastsReset();
        this._setRecurringBroadcastsResetParameters();
    },

    recurringBroadcastsStateUpdate(data) {
        this._setRecurringBroadcastsStateUpdate(data);
    },

    recurringBroadcastsStateUpdateItem({ id, props }) {
        this._setRecurringBroadcastsStateUpdateItem({ id, props });
    },

    recurringBroadcastsStateUpdateDeleteItemForParent({ idChild, idParent, props, type }) {
        this._setRecurringBroadcastsStateUpdateDeleteItemForParent({
            idChild,
            idParent,
            props,
            type,
        });
    },

    recurringBroadcastsUpdateParametersGlobalItems(data) {
        this._setRecurringBroadcastsUpdateParamsGlobalItems(data);
    },

    updateBroadcastsRecurringByIdByStatus({ item, status, form, useAxios = true }) {
        if (useAxios) {
            return baseApi
                .put(`core/broadcasts/recurring/${item.id}/${status}`)
                .then((response) => {
                    const data = new RecurringBroadcast(response.data);

                    this._setRecurringBroadcastsStateUpdateItem({
                        id: data.id,
                        props: data,
                    });

                    return data;
                });
        }

        return form.put(`core/broadcasts/recurring/${item.id}/${status}`).then((response) => {
            const data = new RecurringBroadcast(response.data);

            this._setRecurringBroadcastsStateUpdateItem({
                id: data.id,
                props: data,
            });

            return data;
        });
    },

    updateBroadcastsRecurringByIdRename({ item, form }) {
        return form.put(`core/broadcasts/recurring/${item.id}/rename`).then((response) => {
            const data = new RecurringBroadcast(response.data);

            this._setRecurringBroadcastsStateUpdateItem({
                id: data.id,
                props: data,
            });

            return data;
        });
    },

    // Private methods for all operations
    async _setRecurringBroadcastsUpdateParamsGlobalItems(data) {
        this._parametersGlobalItems = {
            statuses: data,
        };

        for (let i = 0; i < this._items.length; i++) {
            const item = this._items[i];
            await item.reUpdate();
        }

        for (let i = 0; i < this._items.length; i++) {
            const item = this._items[i];
            if (item.toggle) {
                item.load();
            }
        }
    },

    _setRecurringBroadcastsItemsLoaded({ item, data }) {
        let items = new Broadcasts(data.data).models;

        if (item.pagination && !item.pagination.last_page) {
            items = [...item.items, ...items];
        }

        const pagination = {
            current_page: data.meta.current_page,
            from: data.meta.from,
            last_page: data.meta.is_last_page,
            per_page: data.meta.per_page,
            total: data.meta.total,
        };
        const params = {
            page: item.params.page + 1,
        };

        utils.recurringBroadcastsStateUpdateItem(this, {
            id: item.id,
            props: {
                items: items,
                pagination: pagination,
                params: params,
            },
        });
    },

    _setRecurringBroadcastsStateUpdateItem({ id, props }) {
        utils.recurringBroadcastsStateUpdateItem(this, { id, props });
    },

    _setRecurringBroadcastsStateDeleteItem({ id }) {
        utils.recurringBroadcastsStateDeleteItem(this, { id });
    },

    _setRecurringBroadcastsStateUpdateDeleteItemForParent({ idChild, idParent, props, type }) {
        utils.recurringBroadcastsStateUpdateDeleteItemForItems(this, {
            idChild,
            idParent,
            props,
            type,
        });
    },

    _setRecurringBroadcastsStateUpdate(data) {
        for (let key in data) {
            const mappedKey = key.startsWith('_') ? key : `_${key}`;
            this[mappedKey] = data[key];
        }
    },

    _setRecurringBroadcastsLoading(status) {
        this._loading = status;
    },

    _setRecurringBroadcastsRetryLoading() {
        this._items = [];
        this._loading = true;
        this._loadingError = false;

        if (this._pagination) {
            this._pagination.total = 0;
        }
    },

    _setRecurringBroadcastsLoaded({ data }) {
        const items = new RecurringBroadcasts(data.data);

        this._items = items.models;
        this._pagination = {
            current_page: data.meta.current_page,
            from: data.meta.from,
            last_page: data.meta.last_page,
            per_page: data.meta.per_page,
            to: data.meta.to,
            total: data.meta.total,
        };
    },

    _setRecurringBroadcastsUpdateSearch(data) {
        this._parameters.page = 1;
        this._parameters.search = data;
    },

    _setRecurringBroadcastsUpdateFilter(data) {
        this._parameters.filter = data;
    },

    _setRecurringBroadcastsUpdatePage(data) {
        this._parameters.page = data;
    },

    _setRecurringBroadcastsUpdateLength(data) {
        this._parameters.length = data;
    },

    _setRecurringBroadcastsSearchCleared() {
        this._parameters.search = null;
    },

    _setRecurringBroadcastsFiltersApply(filtersList) {
        this._parameters.filtersList = filtersList;
    },

    _setRecurringBroadcastsReset() {
        this._items = [];
        this._loadingError = false;
        this._loading = false;
        this._pagination = null;
    },

    _setRecurringBroadcastsResetParameters() {
        this._parameters = {
            page: 1,
            sortBy: ['disabled', 'created_at'],
            sortOrder: 'desc',
            search: null,
            length: 10,
            filter: null,
            filtersList: [],
        };
    },

    _setRecurringBroadcastsErrorLoading() {
        this._items = [];
        this._loadingError = true;

        if (this._pagination) {
            this._pagination.total = 0;
        }
    },
};

export default {
    state,
    getters,
    actions,
};
